import { Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'chart.js';
import { Subscription } from 'rxjs';
import { UserSessionService } from 'src/app/app-core/services/user-session.service';

import * as dayjs from 'dayjs';

import { GlobalService } from 'src/app/app-core/lib/global.service';
import { GerinusService } from 'src/app/app-core/lib/gerinus.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { RequestService } from 'src/app/app-core/services/request.service';
import { OrganizationService } from 'src/app/app-core/services/organization.service';
import { UserService } from 'src/app/app-core/services/user.service';
import { DashboardService } from 'src/app/app-core/services/dashboard.service';

// core components
import { chartOptions, parseOptions } from '../../variables/charts';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    providers: [
        GerinusService,
        ProcessService,
        RequestService,
        OrganizationService,
        UserService,
        DashboardService,
    ],
})
export class DashboardComponent implements OnInit, OnDestroy {
    chart: Chart;
    lineChart: Chart;

    private perfilAlteradoSub: Subscription;

    requestsAmount: number = 0;
    requestsInEvaluation: number = 0;
    requestsInCorrectionEvaluation: number = 0;
    requestsInCorrectionCitizen: number = 0;
    acceptedRequests: number = 0;

    timelyProcesses: number = 0;
    urgentProcesses: number = 0;
    tranferredProcess: number = 0;
    processesInSector: number = 0;
    processesAmount: number = 0;

    documentsToSign: number = 0;
    organizationsAmount: number = 0;
    usersAmount: number = 0;

    datasets: any;
    data: any;
    salesChart;

    isRememberChoiceChecked: boolean = false;

    /** Perfil oferecido no convite de troca. Preenchido por `avaliarConviteTrocaPerfil`. */
    public perfilSugerido: string = '';

    constructor(
        public service: GerinusService,
        public global: GlobalService,
        public processService: ProcessService,
        public requestService: RequestService,
        public organizationService: OrganizationService,
        public userService: UserService,
        private userSession: UserSessionService,
        private dashboardService: DashboardService,
    ) { }

    async ngOnInit() {
        document.querySelector('title').text = 'Protocolize | Início';

        // Quando a troca parte do modal desta própria tela, a navegação para /inicio não recria
        // o componente — os totais continuariam sendo os do perfil anterior.
        this.perfilAlteradoSub = this.userSession.perfilAlterado$.subscribe(() => {
            this.carregarTotais();
        });

        await this.carregarTotais();

        this.avaliarConviteTrocaPerfil();
    }

    ngOnDestroy() {
        this.perfilAlteradoSub?.unsubscribe();
    }

    /**
     * Os dez totais vêm de uma requisição só.
     *
     * Antes eram dez chamadas, e cada requisição autenticada custa duas consultas de token na
     * API antes de chegar ao trabalho útil — mais de trinta idas ao banco para exibir dez
     * números. Os filtros também deixaram de ser montados aqui: quem sabe o órgão, o
     * departamento e a pessoa é o servidor, a partir de quem está autenticado.
     */
    private async carregarTotais() {
        return new Promise<void>((resolve) => {
            this.dashboardService.getTotaisPainel().subscribe({
                next: (totais: any) => {
                    this.usersAmount = totais?.usuarios ?? 0;
                    this.requestsAmount = totais?.solicitacoes ?? 0;
                    this.requestsInEvaluation = totais?.solicitacoesEmAnalise ?? 0;
                    this.processesAmount = totais?.processos ?? 0;
                    this.processesInSector = totais?.processosNoSetor ?? 0;
                    this.tranferredProcess = totais?.processosTramitados ?? 0;
                    this.organizationsAmount = totais?.orgaos ?? 0;
                    this.requestsInCorrectionEvaluation = totais?.solicitacoesEmCorrecaoAnalise ?? 0;
                    this.requestsInCorrectionCitizen = totais?.solicitacoesEmCorrecaoCidadao ?? 0;
                    this.acceptedRequests = totais?.solicitacoesAceitas ?? 0;

                    this.processesAmount > 0 ? this.createChartProcesso() : '';
                    this.requestsAmount > 0 ? this.createChartRequests() : '';

                    resolve();
                },
                // Falha aqui não pode pendurar o carregamento da tela: os contadores ficam
                // zerados e o resto do painel segue montando.
                error: (erro: any) => {
                    console.error('Erro ao carregar os totais do painel:', erro);
                    resolve();
                },
            });
        });
    }

    createChartProcesso() {
        let processLabels = [];
        let processBackgroundColors = [];
        let processData = [];

        if (this.userService.isDiretorDepartamento() || this.userService.isAdmin()) {
            processLabels = ['Resolvidos', 'Em Análise', 'Arquivados', 'Aguardando Análise', 'Aguardando Correção'];
            processBackgroundColors = ['#0BB07B', '#417bff', '#CDD5E0', '#11cdef', '#fb6340'];

            let countResolvedProcesses = 0;
            let countProcessesInEvaluation = 0;
            let countArchivedProcesses = 0;
            let countProcessWaitingEvaluation = 0;
            let countProcessWaitingCorrection = 0;

            let where = this.userService.isDiretorDepartamento()
                ? `"PRT_DepartamentoAtual" = ${this.global.user().PE_CodigoDepartamento} and "PRT_DataFim" IS NULL`
                : '"PRT_DataFim" IS NULL';

            this.processService.listAll(where, 0).subscribe({
                next: (response: any) => {
                    response.data.forEach((item) => {
                        switch (item.Status) {
                            case 1:
                                countProcessWaitingEvaluation++;
                                break;
                            case 2:
                                countProcessesInEvaluation++;
                                break;
                            case 3:
                                countArchivedProcesses++;
                                break;
                            case 4:
                                countResolvedProcesses++;
                                break;
                            case 5:
                                countProcessWaitingCorrection++;
                                break;
                            default:
                                break;
                        }
                        
                    });

                    processData.push(countResolvedProcesses);
                    processData.push(countProcessesInEvaluation);
                    processData.push(countArchivedProcesses);
                    processData.push(countProcessWaitingEvaluation);
                    processData.push(countProcessWaitingCorrection);

                    this.createChart(processLabels, processData, processBackgroundColors, 'processos');
                },
            });
        }
    }

    createChartRequests() {
        let requestLabels = [];
        let requestBackgroundColors = [];
        let requestsData = [];

        if (this.userService.isCidadao() || this.userService.isAdmin() || this.userService.isTriagem()) {
            requestLabels = ['Aceitas (Processos)', 'Em Triagem', 'Em Correção'];
            requestBackgroundColors = ['#0BB07B', '#417bff', '#e8a21a'];

            let countRequestsInEvaluation = 0;
            let countAcceptedRequests = 0;
            let countRequestsInCorrection = 0;

            let where = '';

            if (this.userService.isTriagem()) {
                where = `"SOL_CodigoOrgao" = ${this.global.user().USR_CodigoOrgao}`;
            } else if (this.userService.isCidadao()) {
                where = `"SOL_CodigoSolicitante" = ${this.global.user().PE_CodigoPessoa}`;
            }

            this.requestService.listAll(where, 0).subscribe({
                next: (response: any) => {
                    response.data.forEach((item) => {
                        if (item.SOL_Status === 1) {
                            countAcceptedRequests++;
                        } else if (item.SOL_Status === 0) {
                            countRequestsInEvaluation++;
                        } else if (item.SOL_Status === 2) {
                            countRequestsInCorrection++;
                        }
                    });

                    requestsData.push(countAcceptedRequests);
                    requestsData.push(countRequestsInEvaluation);
                    requestsData.push(countRequestsInCorrection);

                    this.createChart(requestLabels, requestsData, requestBackgroundColors, 'solicitacoes');
                },
            });
        }

        if (this.userService.isAdmin() || this.userService.isTriagem()) {
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1;

            let where = '';

            if (this.userService.isTriagem()) {
                where = `"SOL_CodigoOrgao" = ${this.global.user().USR_CodigoOrgao}`;
            }

            this.requestService.listAll(where, 0).subscribe({
                next: (response: any) => {
                    const dias = Array.from({ length: 31 }, (_, i) => (i + 1).toString()); // Assumindo um máximo de 31 dias

                    const solicitacoesPorDia = new Array(dias.length).fill(0);

                    response.data.forEach((solicitacao) => {
                        const dataSolicitacao = new Date(solicitacao.SOL_DataSolicitacao);
                        if (dataSolicitacao.getMonth() + 1 === currentMonth) {
                            const dia = dataSolicitacao.getDate();
                            solicitacoesPorDia[dia - 1]++; // Ajustado para adicionar 1 ao índice
                        }
                    });

                    this.createBarChart(
                        dias.slice(0, currentDate.getDate()),
                        solicitacoesPorDia.slice(0, currentDate.getDate()),
                    );
                },
            });
        }
    }

    createChart(labels, data, backgroundColor, chartName) {
        setTimeout(() => {
          this.chart = new Chart(chartName, {
            type: 'doughnut',
            data: {
              labels,
              datasets: [
                {
                  data,
                  backgroundColor,
                },
              ],
            },
            options: {
              plugins: {
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      let label = context.label || '';
                      if (label) {
                        label += ': ';
                      }
                      label += context.raw + ' (' + ((context.raw / data.reduce((a, b) => a + b, 0)) * 100).toFixed(2) + '%)';
                      return label;
                    }
                  }
                },
                legend: {
                  display: true,
                  position: 'top',
                  labels: {
                    generateLabels: function (chart) {
                      const data = chart.data;
                      if (data.labels.length && data.datasets.length) {
                        return data.labels.map((label, i) => {
                          const meta = chart.getDatasetMeta(0);
                          const dataset = data.datasets[0];
                          const value = dataset.data[i];
                          const total = dataset.data.reduce((a, b) => a + b, 0);
                          const percentage = ((value / total) * 100).toFixed(2);
      
                          return {
                            text: `${label}: ${percentage}%`,
                            fillStyle: dataset.backgroundColor[i],
                            strokeStyle: dataset.backgroundColor[i],
                            index: i
                          };
                        });
                      }
                      return [];
                    },
                    usePointStyle: true,
                    padding: 16,
                  },
                  onClick: null // Disable click functionality
                }
              }
            }
          });
        }, 1000);
      }

    createBarChart(days, requestsPerDay) {
        var chartOrders = document.getElementById('solicitacoes-bar-chart');

        parseOptions(Chart, chartOptions());

        setTimeout(() => {
            this.salesChart = new Chart(chartOrders, {
                type: 'bar',
                options: {
                    tooltips: {
                        callbacks: {
                            label: function (tooltipItem, data) {
                                const solicitacoes = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                                return `${solicitacoes} ${solicitacoes == 1 ? 'Solicitação' : 'Solicitações'}`;
                            },
                        },
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        yAxes: [
                            {
                                ticks: {
                                    beginAtZero: true,
                                    stepSize: 2,
                                },
                            },
                        ],
                        xAxes: [
                            {
                                ticks: {
                                    callback: function (value, index, values) {
                                        const currentDate = dayjs().format('MM');
                                        const formattedDate = value.toString().padStart(2, '0') + '/' + currentDate;
                                        return formattedDate;
                                    },
                                },
                            },
                        ],
                    },
                },
                data: {
                    labels: days,
                    datasets: [
                        {
                            data: requestsPerDay,
                        },
                    ],
                },
            });
        }, 1000);
    }


    /**
     * Decide se o convite de troca de perfil deve aparecer, a partir dos perfis realmente
     * vinculados ao cadastro. A consulta acontece uma vez, aqui — antes o valor vinha de um
     * getter lido pelo template a cada ciclo de change detection, e de um retrato do login
     * que podia oferecer um perfil que o servidor já não aceitava.
     */
    private avaliarConviteTrocaPerfil() {
        this.userService.perfisDisponiveisAtualizados().subscribe({
            next: (perfis) => {
                this.perfilSugerido = perfis[0]?.nome ?? '';

                const isRememberChoice = this.global.user().USR_LembrarEscolha;
                const isModalChangeGroupDisplayed = localStorage.getItem('modal-change-group-displayed');

                if (
                    this.global.user().USR_UsuarioInterno &&
                    !this.global.user().loginAlternativo &&
                    isRememberChoice == false &&
                    isModalChangeGroupDisplayed !== 'true' &&
                    // Sem perfil alternativo vinculado não há o que oferecer: antes o modal abria
                    // assim mesmo, propondo a troca para um perfil que a pessoa não possuía.
                    this.perfilSugerido !== ''
                ) {
                    localStorage.setItem('modal-change-group-displayed', 'true');
                    this.global.showModalInfo('change-user-group');
                }
            },
        });
    }

    closeModalChangeUserGroup(rememberChoice: boolean) {
        this.global.closeModalInfo('change-user-group');

        if (rememberChoice) {
            this.userService.rememberChoice(this.global.user().USR_CodigoUsuario).subscribe((response: any) => {
                console.log('USUÁRIO ATUALIZADO', response);
            });
        }
    }

    toggleCheckRememberChoice() {
        this.isRememberChoiceChecked = !this.isRememberChoiceChecked;
    }

    redirectToNewRequest() {
        this.global.router.navigate(['/requests/new']);
    }
}
